import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DropZone from '@src/new-components/DropZone';
import { handleCheckQualityAudio } from '@src/services/instantVoiceCloning';
import sanitizeHtml from 'sanitize-html';
import Typography from '@src/new-components/Typography';
import CommonButton from '@src/new-components/Button';
import { COLOR } from '@src/styles/color';
import useFakeProgress from '@src/hooks/useFakeProgress';
import { Stack } from '@mui/material';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import MicIcon from '@mui/icons-material/Mic';
import {
  MIN_DURATION,
  MAX_DURATION,
  STEPS,
} from '@src/constants/instantVoiceCloning';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import { ContentUpload, StyledErrorUpload } from './index.style';
import ProcessingAudio from '../ProcessingAudio';

const ErrorUpload = ({ errorUpload }) => {
  const { t } = useTranslation();

  return (
    <StyledErrorUpload>
      <Typography
        component="p"
        variant="body-14-regular"
        color={COLOR.errorDropZone}
      >
        {t(errorUpload.key, {
          fileName: errorUpload.fileName,
          minDuration: MIN_DURATION,
          maxDuration: MAX_DURATION,
          size: '10MB',
        })}
      </Typography>
    </StyledErrorUpload>
  );
};

const MAX_FAKE_PROGRESS_TIME = 10000;
const FAKE_PROGRESS_INTERVAL = 100;

const VoiceSampleUploader = ({ onSetMetadataAudio, onSetStep }) => {
  const { t } = useTranslation();
  const [errorUpload, setErrorUpload] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { progress, start, stop, reset } = useFakeProgress({
    interval: FAKE_PROGRESS_INTERVAL,
    maxProgress: 100,
    maxTime: MAX_FAKE_PROGRESS_TIME,
  });

  useEffect(
    () => () => {
      reset();
    },
    [reset],
  );

  const validateAudioQuality = async (file) => {
    setIsLoading(true);
    setErrorUpload(null);
    start();
    const result = await handleCheckQualityAudio({ fileUpload: file });
    stop();
    await new Promise((resolve) => setTimeout(resolve, 500));
    setIsLoading(false);
    if (!result?.isQuality) {
      setErrorUpload({ key: 'invalidNoAudio' });
      return false;
    }
    return result;
  };

  const onDrop = async (file) => {
    const result = await validateAudioQuality(file);
    const { isQuality, audioId, s3Url } = result || {};

    if (isQuality) {
      onSetMetadataAudio({
        audioLink: s3Url,
        audioId,
        fileName: file.name,
      });
      onSetStep(STEPS.PREVIEW_AUDIO);
    }
  };

  if (isLoading) {
    return <ProcessingAudio title={t('uploadingAudio')} progress={progress} />;
  }

  return (
    <DropZone
      accept=".wav,.mp3,.m4a"
      height={360}
      validatorKeys={['format', 'size', 'duration']}
      onSetError={setErrorUpload}
      isError={!!errorUpload?.key}
      onDrop={onDrop}
    >
      <ContentUpload>
        <div style={{ marginBottom: '16px' }}>
          <AddCircleOutlineRoundedIcon
            sx={{
              color: COLOR.indigoIcon,
              fontSize: '24px !important',
            }}
          />
        </div>
        <Typography
          component="p"
          variant="body-16-medium"
          color={COLOR.colorIcon}
        >
          {t('instantVoiceCloneV2')}
        </Typography>
        <div style={{ marginBottom: '24px', marginTop: '4px' }}>
          <Typography component="p" variant="body-14-regular">
            <div
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(t('uploadFileDesscription')),
              }}
              style={{
                color: COLOR.descriptionUpload,
              }}
            />
          </Typography>
        </div>
        {errorUpload?.key && <ErrorUpload errorUpload={errorUpload} />}
        <Stack direction="row" justifyContent="center" gap={2}>
          <CommonButton
            size="small"
            variant="outlined"
            startIcon={
              <CloudUploadRoundedIcon sx={{ fontSize: '24px !important' }} />
            }
            textProps={{
              variantText: 'body-14-bold',
              color: COLOR.darkBlue,
            }}
            customStyle={{
              height: '48px',
            }}
          >
            {t('uploadDocuments')}
          </CommonButton>
          <CommonButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onSetStep(STEPS.RECORD_AUDIO);
            }}
            variant="contained"
            startIcon={<MicIcon sx={{ fontSize: '24px !important' }} />}
            textProps={{
              variantText: 'body-14-bold',
              color: COLOR.darkBlue,
            }}
            customStyle={{
              height: '48px',
            }}
          >
            {t('recordAudio')}
          </CommonButton>
        </Stack>
      </ContentUpload>
    </DropZone>
  );
};

export default VoiceSampleUploader;
